// JOBE-0056.10: operator console — read-only viewer for the most-recent
// context bundles assembled by convdispatcher.Builder. The data source
// is a single in-process ring buffer (operator/convdispatcher.RecentBundles)
// surfaced through GET /api/admin/convdispatcher/recent[/{id}].
//
// Empty-state copy is intentional and load-bearing: when the dispatcher
// Builder is not wired into the wake-trigger bridge (the JOBE-0056.9
// default), the buffer is empty and the page must say so plainly. There
// is NO synthetic seed data anywhere on this page.

import { useEffect, useMemo, useState } from 'react';
import { useOutletContext, useParams, Link, useNavigate } from 'react-router-dom';
import { useDocumentTitle } from './lib/useDocumentTitle.js';

const POLL_INTERVAL_MS = 5000;

// SECTION_KEYS drives both the stacked-bar widget and the per-section
// detail panes. Order matches the assembly pipeline (Builder.Assemble)
// so operators read the chart left-to-right in the same order events
// flow into the bundle.
const SECTION_KEYS = [
  { key: 'foreground',       label: 'Foreground',       color: 'var(--c-fg)' },
  { key: 'siblings',         label: 'Siblings',         color: 'var(--c-siblings)' },
  { key: 'inbox_and_pinned', label: 'Inbox + Pinned',   color: 'var(--c-inbox)' },
  { key: 'artifacts',        label: 'Artifacts',        color: 'var(--c-artifacts)' },
  { key: 'notes',            label: 'Notes',            color: 'var(--c-notes)' },
  { key: 'triggering_event', label: 'Triggering event', color: 'var(--c-trigger)' },
];

function formatTime(t) {
  if (!t) return '';
  try {
    return new Date(t).toLocaleTimeString();
  } catch (_e) {
    return t;
  }
}

function shortID(id) {
  if (!id) return '—';
  return id.length > 12 ? id.slice(0, 12) + '…' : id;
}

function totalUsed(costs) {
  if (!costs) return 0;
  return SECTION_KEYS.reduce((sum, s) => sum + (costs[s.key] || 0), 0);
}

function unitLabel(costs) {
  if (!costs) return 'tokens';
  return costs.estimator_wired ? 'tokens' : 'bytes';
}

// EmptyState renders the explicit "no recent bundles" message. The copy
// is the contract from the parent spec — do not paraphrase without
// updating the spec.
function EmptyState() {
  return (
    <div className="card dispatcher-empty">
      <h3>Dispatcher · recent bundles</h3>
      <p>
        No recent bundles. The dispatcher Builder is not currently wired
        into <code>wake_trigger_bridge</code> — bundles will appear here
        once it is.
      </p>
      <p className="dispatcher-empty-hint">
        See <code>convdispatcher.Builder</code> in <code>operator/convdispatcher</code>{' '}
        and the <code>Bundler</code> field on <code>conv.WakeTriggerBridge</code>.
      </p>
    </div>
  );
}

function ListView({ items }) {
  // Build a unique-agent picker from the recorded items. "all" is the
  // default. We deliberately don't fetch agent registries here — the
  // picker shows whichever agents have been observed.
  const [agent, setAgent] = useState('all');

  const agents = useMemo(() => {
    const seen = new Set();
    for (const it of items) {
      const name = it.request?.AgentName || it.request?.agent_name;
      if (name) seen.add(name);
    }
    return ['all', ...Array.from(seen).sort()];
  }, [items]);

  const filtered = useMemo(() => {
    if (agent === 'all') return items;
    return items.filter((it) => {
      const name = it.request?.AgentName || it.request?.agent_name;
      return name === agent;
    });
  }, [items, agent]);

  return (
    <div className="card">
      <div className="dispatcher-header">
        <h3>Dispatcher · recent bundles</h3>
        <label className="dispatcher-agent-picker">
          <span>Agent:</span>
          <select value={agent} onChange={(e) => setAgent(e.target.value)}>
            {agents.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
      </div>
      <table className="dispatcher-table">
        <thead>
          <tr>
            <th>Time</th>
            <th>AgentRunID</th>
            <th>Agent</th>
            <th>ConvID</th>
            <th>Thread URN</th>
            <th>Used / Total</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr><td colSpan={7} className="dispatcher-empty-row">No bundles for this agent.</td></tr>
          ) : filtered.map((it) => {
            const id = it.request?.AgentRunID || it.request?.agent_run_id;
            const used = it.bundle?.BudgetRemaining?.Used ?? 0;
            const total = it.bundle?.BudgetRemaining?.Total ?? 0;
            // When AgentRunID is missing/empty we can't link to a detail
            // route (encodeURIComponent(undefined) → "undefined"), so
            // render plain text in the same slot to keep the row shape.
            return (
              <tr key={id || it.recorded_at}>
                <td>{formatTime(it.recorded_at)}</td>
                <td>
                  {id
                    ? <Link to={`/admin/dispatcher/${encodeURIComponent(id)}`}>{shortID(id)}</Link>
                    : <span className="muted">{shortID(id)}</span>}
                </td>
                <td>{it.request?.AgentName || '—'}</td>
                <td>{shortID(it.bundle?.ConvID)}</td>
                <td>{shortID(it.bundle?.ThreadURN)}</td>
                <td>{used} / {total || '∞'}</td>
                <td>{it.err ? <span className="badge badge-err">{it.err}</span> : <span className="badge badge-ok">ok</span>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StackedBar({ costs }) {
  const total = totalUsed(costs);
  // Render a zero-state bar so the layout doesn't collapse; the legend
  // still labels the sections so operators learn the colour key.
  if (total <= 0) {
    return (
      <div className="dispatcher-bar dispatcher-bar-empty">
        (all sections empty)
      </div>
    );
  }
  return (
    <div className="dispatcher-bar">
      {SECTION_KEYS.map((s) => {
        const v = costs[s.key] || 0;
        if (v <= 0) return null;
        const pct = (v / total) * 100;
        return (
          <div
            key={s.key}
            className="dispatcher-bar-seg"
            style={{ width: `${pct}%`, background: s.color }}
            title={`${s.label}: ${v} ${unitLabel(costs)} (${pct.toFixed(1)}%)`}
          />
        );
      })}
    </div>
  );
}

function SectionLegend({ costs }) {
  const u = unitLabel(costs);
  return (
    <ul className="dispatcher-legend">
      {SECTION_KEYS.map((s) => (
        <li key={s.key}>
          <span className="swatch" style={{ background: s.color }} />
          {s.label}: <strong>{costs[s.key] || 0}</strong> {u}
        </li>
      ))}
    </ul>
  );
}

function SectionPane({ title, items, render, emptyMsg }) {
  return (
    <div className="card dispatcher-section">
      <h4>{title}</h4>
      {(!items || items.length === 0)
        ? <p className="muted">{emptyMsg || 'empty'}</p>
        : <ul className="dispatcher-events">{items.map(render)}</ul>}
    </div>
  );
}

function severityBadgeClass(sev) {
  // Note severity numbers come from the Go side: 0 = info, 1 = warn.
  // We render them with descriptive labels and styled badges so
  // operators don't have to remember the integer mapping.
  switch (sev) {
    case 1: return 'badge badge-warn';
    case 0:
    default: return 'badge badge-info';
  }
}

function severityLabel(sev) {
  switch (sev) {
    case 1: return 'warn';
    case 0:
    default: return 'info';
  }
}

function DetailView({ rec }) {
  if (!rec) return null;
  const costs = rec.section_costs || {};
  const fg = rec.bundle?.Foreground || [];
  const siblings = rec.bundle?.SiblingSummaries || [];
  const pinned = rec.bundle?.Pinned || [];
  const inboxRows = rec.bundle?.Inbox?.Rows || [];
  const artifacts = rec.bundle?.Artifacts || [];
  const notes = rec.bundle?.Notes || [];
  const trig = rec.bundle?.TriggeringEvent;

  return (
    <>
      <div className="card">
        <div className="dispatcher-header">
          <h3>Bundle · {shortID(rec.request?.AgentRunID)}</h3>
          <Link to="/admin/dispatcher" className="dispatcher-back">← back to list</Link>
        </div>
        <dl className="dispatcher-meta">
          <dt>Recorded</dt>     <dd>{formatTime(rec.recorded_at)}</dd>
          <dt>Latency</dt>      <dd>{Math.round((rec.latency || 0) / 1e6)} ms</dd>
          <dt>Agent</dt>        <dd>{rec.request?.AgentName || '—'}</dd>
          <dt>Principal</dt>    <dd>{rec.request?.PrincipalID || '—'}</dd>
          <dt>ConvID</dt>       <dd>{rec.bundle?.ConvID || '—'}</dd>
          <dt>Thread URN</dt>   <dd>{rec.bundle?.ThreadURN || '—'}</dd>
          <dt>Budget</dt>       <dd>
            {rec.bundle?.BudgetRemaining?.Used ?? 0} / {rec.bundle?.BudgetRemaining?.Total || '∞'} {unitLabel(costs)}
          </dd>
          {rec.err && (<><dt>Error</dt><dd className="dispatcher-err">{rec.err}</dd></>)}
        </dl>

        {!costs.estimator_wired && (
          <p className="muted dispatcher-no-estimator">
            Estimator not configured — section sizes are byte counts, not token counts.
          </p>
        )}
        <StackedBar costs={costs} />
        <SectionLegend costs={costs} />
      </div>

      <SectionPane
        title="Triggering event"
        items={trig ? [trig] : []}
        emptyMsg="(no triggering event)"
        render={(ev, i) => (
          <li key={i}>
            <code>{ev.EventType || '?'}</code>{' '}
            <span className="muted">streamSeq={ev.StreamSeq ?? 0}, body={ev.Body ? atob(ev.Body).length : 0}B</span>
          </li>
        )}
      />

      <SectionPane
        title={`Foreground (${fg.length})`}
        items={fg}
        emptyMsg="No foreground events. Renderer may be unwired or this was a new-thread wake."
        render={(ev, i) => (
          <li key={i}>
            <code>{ev.EventType || '?'}</code>{' '}
            <span className="muted">streamSeq={ev.StreamSeq ?? 0}, body={ev.Body ? atob(ev.Body).length : 0}B</span>
          </li>
        )}
      />

      <SectionPane
        title={`Sibling summaries (${siblings.length})`}
        items={siblings}
        emptyMsg="No sibling summaries."
        render={(ts, i) => (
          <li key={i}>
            <code>{ts.ThreadURN}</code>{' '}
            {ts.Stale && <span className="badge badge-warn">stale</span>}{' '}
            <span className="muted">{ts.SummaryText?.slice(0, 80)}</span>
          </li>
        )}
      />

      <SectionPane
        title={`Inbox rows (${inboxRows.length})`}
        items={inboxRows}
        emptyMsg="No inbox rows."
        render={(row, i) => (
          <li key={i}>
            <code>{row.ChannelID}</code> · unread={row.UnreadCount}{' '}
            <span className="muted">{row.MostRecentPreview}</span>
          </li>
        )}
      />

      <SectionPane
        title={`Pinned (${pinned.length})`}
        items={pinned}
        emptyMsg="No pinned events."
        render={(ev, i) => (
          <li key={i}>
            <code>{ev.EventType || '?'}</code>{' '}
            <span className="muted">streamSeq={ev.StreamSeq ?? 0}</span>
          </li>
        )}
      />

      <SectionPane
        title={`Artifacts (${artifacts.length})`}
        items={artifacts}
        emptyMsg="No artifacts."
        render={(ar, i) => (
          <li key={i}>
            <code>{ar.ArtifactID}</code> · mode={modeLabel(ar.Mode)}{' '}
            <span className="muted">{ar.Bytes ? `${atob(ar.Bytes).length}B` : ''}</span>
          </li>
        )}
      />

      <SectionPane
        title={`Dispatcher notes (${notes.length})`}
        items={notes}
        emptyMsg="No dispatcher notes."
        render={(n, i) => (
          <li key={i}>
            <span className={severityBadgeClass(n.Severity)}>{severityLabel(n.Severity)}</span>{' '}
            <code>{n.Kind}</code> — {n.Message}
          </li>
        )}
      />
    </>
  );
}

function modeLabel(m) {
  switch (m) {
    case 0: return 'inline';
    case 1: return 'summary';
    case 2: return 'reference';
    default: return `mode(${m})`;
  }
}

// useDispatcherList polls the list endpoint at POLL_INTERVAL_MS. Returns
// { items, error, loading }. items is an array (possibly empty) once the
// first fetch completes.
function useDispatcherList() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const ctrl = new AbortController();
    const run = () => {
      fetch('/api/admin/convdispatcher/recent', { credentials: 'same-origin', signal: ctrl.signal })
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then((j) => {
          setItems(Array.isArray(j?.items) ? j.items : []);
          setError(null);
        })
        .catch((err) => {
          if (err.name === 'AbortError') return;
          setError(err.message);
        });
    };
    run();
    const id = setInterval(run, POLL_INTERVAL_MS);
    return () => { clearInterval(id); ctrl.abort(); };
  }, []);

  return { items, error, loading: items === null };
}

function useDispatcherDetail(id) {
  const [rec, setRec] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) return undefined;
    // Reset on id change so navigating between bundle detail pages
    // doesn't briefly render stale data from the previous bundle.
    setRec(null);
    setError(null);
    const ctrl = new AbortController();
    fetch(`/api/admin/convdispatcher/recent/${encodeURIComponent(id)}`, {
      credentials: 'same-origin', signal: ctrl.signal,
    })
      .then((r) => {
        if (r.status === 404) { setRec(undefined); return null; }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((j) => { if (j) setRec(j); })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setError(err.message);
      });
    return () => ctrl.abort();
  }, [id]);

  return { rec, error };
}

export function DispatcherConsole() {
  // JOBE-0193: per-route document title.
  useDocumentTitle('Dispatcher — Admin');
  // Outlet context isn't required, but pull it so the page can adopt the
  // standard agent map if it ever needs it. Safe-guarded for the
  // possibility of being mounted outside the Shell layout (tests).
  let ctx = {};
  try { ctx = useOutletContext() || {}; } catch (_e) { /* not mounted in Shell */ }
  void ctx;

  const { items, error, loading } = useDispatcherList();

  if (error) {
    return <div className="card"><h3>Dispatcher · recent bundles</h3><p className="errorMsg">Failed to load: {error}</p></div>;
  }
  if (loading) {
    return <div className="card"><h3>Dispatcher · recent bundles</h3><p>Loading…</p></div>;
  }
  if (items.length === 0) return <EmptyState />;
  return <ListView items={items} />;
}

export function DispatcherBundleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { rec, error } = useDispatcherDetail(id);

  if (error) {
    return <div className="card"><h3>Bundle · {shortID(id)}</h3><p className="errorMsg">Failed to load: {error}</p></div>;
  }
  if (rec === undefined) {
    return (
      <div className="card">
        <h3>Bundle · {shortID(id)}</h3>
        <p>Not found — this run may have been evicted from the in-memory ring buffer.</p>
        <button onClick={() => navigate('/admin/dispatcher')}>Back to list</button>
      </div>
    );
  }
  if (rec === null) {
    return <div className="card"><h3>Bundle · {shortID(id)}</h3><p>Loading…</p></div>;
  }
  return <DetailView rec={rec} />;
}
